import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  DollarSign,
  Building2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Download,
  Edit,
  Check,
  X,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Users,
} from 'lucide-react';
import type { RootState } from '@/store';
import { divisionsApi } from '@/utils/api';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  GlassLoadingSpinner,
  GlassSelect,
  SectionReveal,
  MetricCard,
  ProgressBar,
  CollapsibleMetricsSection,
} from '@/design-system';
import { UserRole } from '@/types';
import { formatCurrencyCompact, formatPercent } from '@/utils/formatters';

// Types for budget data
interface ProgramBudgetSummary {
  programId: string;
  programName: string;
  budget: number;
  laborBudget: number | null;
  nonLaborBudget: number | null;
  committedCost: number;
  spentCost: number;
  remainingBudget: number;
  teamWeeksAllocated: number;
  budgetUtilizationPercent: number;
  spentPercent: number;
  epicCount: number;
  allocationCount: number;
}

interface DivisionBudgetSummary {
  division: {
    id: string;
    name: string;
    budgetCode: string | null;
    fiscalYear: number | null;
  };
  budget: {
    total: number;
    allocatedToPrograms: number;
    allocatedToProgramsPercent: number;
    unallocatedToPrograms: number;
  };
  costs: {
    committed: number;
    committedPercent: number;
    spent: number;
    spentPercent: number;
    remaining: number;
  };
  capacity: {
    totalTeamWeeksAllocated: number;
  };
  status: 'ON_TRACK' | 'AT_RISK' | 'OVER_BUDGET' | 'UNDER_UTILIZED';
  programCount: number;
  programs: ProgramBudgetSummary[];
}

interface DivisionWithBudget {
  id: string;
  name: string;
  budgetCode: string | null;
  annualBudget: number | null;
  budgetFiscalYear: number | null;
  _count?: { programs: number };
}


const getStatusBadge = (status: string) => {
  switch (status) {
    case 'ON_TRACK':
      return <GlassBadge variant="success" size="sm">On Track</GlassBadge>;
    case 'AT_RISK':
      return <GlassBadge variant="warning" size="sm">At Risk</GlassBadge>;
    case 'OVER_BUDGET':
      return <GlassBadge variant="danger" size="sm">Over Budget</GlassBadge>;
    case 'UNDER_UTILIZED':
      return <GlassBadge variant="info" size="sm">Under Utilized</GlassBadge>;
    default:
      return <GlassBadge variant="default" size="sm">{status}</GlassBadge>;
  }
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'ON_TRACK':
      return 'border-emerald-500 bg-emerald-50/50';
    case 'AT_RISK':
      return 'border-amber-500 bg-amber-50/50';
    case 'OVER_BUDGET':
      return 'border-rose-500 bg-rose-50/50';
    case 'UNDER_UTILIZED':
      return 'border-blue-500 bg-blue-50/50';
    default:
      return 'border-gray-500 bg-gray-50/50';
  }
};

const DivisionBudgetManagementPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [divisions, setDivisions] = useState<DivisionWithBudget[]>([]);
  const [budgetSummaries, setBudgetSummaries] = useState<Map<string, DivisionBudgetSummary>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(new Set());
  const [editingDivision, setEditingDivision] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ annualBudget: string; budgetFiscalYear: string }>({
    annualBudget: '',
    budgetFiscalYear: '',
  });
  const [saving, setSaving] = useState(false);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(new Date().getFullYear());

  // Check permission
  const canEdit = user?.role === UserRole.ADMIN || user?.role === UserRole.FINANCE;
  const canView = canEdit;

  // Generate fiscal year options
  const fiscalYearOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [];
    for (let year = 2020; year <= 2030; year++) {
      years.push({ value: year.toString(), label: `FY${year}` });
    }
    return years;
  }, []);

  const fetchDivisions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await divisionsApi.getAll();
      if (response.success && response.data) {
        const divisionsList = (response.data as { divisions: DivisionWithBudget[] }).divisions || [];
        setDivisions(divisionsList);
      } else {
        setError((response.error as { message: string })?.message || 'Failed to fetch divisions');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch divisions');
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetSummary = async (divisionId: string) => {
    try {
      const response = await divisionsApi.getBudgetSummary(divisionId, selectedFiscalYear);
      if (response.success && response.data) {
        const summary = (response.data as { budgetSummary: DivisionBudgetSummary }).budgetSummary;
        setBudgetSummaries(prev => new Map(prev).set(divisionId, summary));
      }
    } catch (err) {
      console.error('Failed to fetch budget summary for division', divisionId, err);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchDivisions();
    }
  }, [canView]);

  useEffect(() => {
    // Fetch budget summaries for expanded divisions when fiscal year changes
    expandedDivisions.forEach(divisionId => {
      fetchBudgetSummary(divisionId);
    });
  }, [selectedFiscalYear]);

  const toggleDivision = async (divisionId: string) => {
    const newExpanded = new Set(expandedDivisions);
    if (newExpanded.has(divisionId)) {
      newExpanded.delete(divisionId);
    } else {
      newExpanded.add(divisionId);
      // Fetch budget summary if not already loaded
      if (!budgetSummaries.has(divisionId)) {
        await fetchBudgetSummary(divisionId);
      }
    }
    setExpandedDivisions(newExpanded);
  };

  const handleStartEdit = (division: DivisionWithBudget) => {
    setEditingDivision(division.id);
    setEditValues({
      annualBudget: division.annualBudget?.toString() || '',
      budgetFiscalYear: division.budgetFiscalYear?.toString() || selectedFiscalYear.toString(),
    });
  };

  const handleCancelEdit = () => {
    setEditingDivision(null);
    setEditValues({ annualBudget: '', budgetFiscalYear: '' });
  };

  const handleSaveEdit = async (divisionId: string) => {
    try {
      setSaving(true);
      const data: { annualBudget?: number; budgetFiscalYear?: number } = {};

      if (editValues.annualBudget) {
        data.annualBudget = parseFloat(editValues.annualBudget);
      }
      if (editValues.budgetFiscalYear) {
        data.budgetFiscalYear = parseInt(editValues.budgetFiscalYear, 10);
      }

      const response = await divisionsApi.updateBudget(divisionId, data);
      if (response.success) {
        // Refresh divisions list
        await fetchDivisions();
        // Refresh budget summary if expanded
        if (expandedDivisions.has(divisionId)) {
          await fetchBudgetSummary(divisionId);
        }
        setEditingDivision(null);
        setEditValues({ annualBudget: '', budgetFiscalYear: '' });
      } else {
        setError((response.error as { message: string })?.message || 'Failed to update budget');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to update budget');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchDivisions();
    // Clear and refetch all expanded summaries
    expandedDivisions.forEach(divisionId => {
      fetchBudgetSummary(divisionId);
    });
  };

  const handleExportCSV = () => {
    const csvRows: string[][] = [
      ['Division Budget Management Report'],
      [`Fiscal Year: FY${selectedFiscalYear}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ['Division', 'Budget Code', 'Annual Budget', 'FY', 'Programs', 'Allocated to Programs', 'Committed', 'Spent', 'Status'],
    ];

    divisions.forEach(division => {
      const summary = budgetSummaries.get(division.id);
      csvRows.push([
        division.name,
        division.budgetCode || '',
        (division.annualBudget || 0).toString(),
        division.budgetFiscalYear?.toString() || '',
        (division._count?.programs || 0).toString(),
        summary ? summary.budget.allocatedToPrograms.toString() : '',
        summary ? summary.costs.committed.toString() : '',
        summary ? summary.costs.spent.toString() : '',
        summary ? summary.status : '',
      ]);
    });

    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `division-budgets-FY${selectedFiscalYear}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const totalBudget = divisions.reduce((sum, d) => sum + (d.annualBudget || 0), 0);
    let totalCommitted = 0;
    let totalSpent = 0;
    let atRiskCount = 0;
    let overBudgetCount = 0;

    budgetSummaries.forEach(summary => {
      totalCommitted += summary.costs.committed;
      totalSpent += summary.costs.spent;
      if (summary.status === 'AT_RISK') atRiskCount++;
      if (summary.status === 'OVER_BUDGET') overBudgetCount++;
    });

    return {
      totalBudget,
      totalCommitted,
      totalSpent,
      divisionCount: divisions.length,
      atRiskCount,
      overBudgetCount,
      utilizationPercent: totalBudget > 0 ? (totalCommitted / totalBudget) * 100 : 0,
    };
  }, [divisions, budgetSummaries]);

  if (!canView) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassCard className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            This page is only accessible to Finance and Admin users.
          </p>
        </GlassCard>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading divisions..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Division Budget Management"
          subtitle="Set and manage division-level budget allocations"
          icon={<DollarSign className="w-6 h-6" />}
          iconGradient="rose"
          className="mb-6"
          actions={
            <div className="flex items-center gap-3">
              <GlassSelect
                value={selectedFiscalYear.toString()}
                onChange={(value) => setSelectedFiscalYear(parseInt(value, 10))}
                options={fiscalYearOptions}
                size="sm"
              />
              <GlassButton
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                leftIcon={<RefreshCw className="w-4 h-4" />}
              >
                Refresh
              </GlassButton>
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={handleExportCSV}
                leftIcon={<Download className="w-4 h-4" />}
              >
                Export CSV
              </GlassButton>
            </div>
          }
        />
      </SectionReveal>

      {error && (
        <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-xl mb-6">
          <p className="text-status-error text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Summary Metrics */}
      <SectionReveal delay={0.1}>
        <CollapsibleMetricsSection storageKey="division-budget">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard
              title="Total Portfolio Budget"
              value={formatCurrencyCompact(summaryMetrics.totalBudget)}
              icon={<DollarSign className="w-5 h-5" />}
              iconColor="violet"
            />
            <MetricCard
              title="Committed"
              value={formatCurrencyCompact(summaryMetrics.totalCommitted)}
              subtitle={formatPercent(summaryMetrics.utilizationPercent)}
              icon={<TrendingUp className="w-5 h-5" />}
              iconColor="cyan"
            />
            <MetricCard
              title="Spent"
              value={formatCurrencyCompact(summaryMetrics.totalSpent)}
              icon={<TrendingDown className="w-5 h-5" />}
              iconColor="emerald"
            />
            <MetricCard
              title="Divisions"
              value={summaryMetrics.divisionCount.toString()}
              icon={<Building2 className="w-5 h-5" />}
              iconColor="amber"
            />
            <MetricCard
              title="At Risk / Over Budget"
              value={`${summaryMetrics.atRiskCount} / ${summaryMetrics.overBudgetCount}`}
              icon={<AlertTriangle className="w-5 h-5" />}
              iconColor={summaryMetrics.overBudgetCount > 0 ? 'rose' : 'emerald'}
            />
          </div>
        </CollapsibleMetricsSection>
      </SectionReveal>

      {/* Divisions List */}
      <SectionReveal delay={0.2}>
        <GlassCard padding="none">
          <div className="divide-y divide-white/10">
            {divisions.map((division, index) => {
              const summary = budgetSummaries.get(division.id);
              const isExpanded = expandedDivisions.has(division.id);
              const isEditing = editingDivision === division.id;

              return (
                <div key={division.id} className="section-reveal" style={{ animationDelay: `${300 + index * 50}ms` }}>
                  {/* Division Header Row */}
                  <div
                    className={`p-5 hover:bg-white/5 transition-colors ${
                      summary ? `border-l-4 ${getStatusColor(summary.status)}` : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <button
                          onClick={() => toggleDivision(division.id)}
                          className="p-1 hover:bg-white/10 rounded transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          )}
                        </button>
                        <div>
                          <h3 className="text-base font-semibold text-foreground">{division.name}</h3>
                          {division.budgetCode && (
                            <span className="text-xs text-muted-foreground/60">Code: {division.budgetCode}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {/* Budget Amount */}
                        <div className="text-right min-w-[140px]">
                          <p className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">Annual Budget</p>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editValues.annualBudget}
                              onChange={(e) => setEditValues(prev => ({ ...prev, annualBudget: e.target.value }))}
                              className="mt-1 w-full px-2 py-1 text-sm bg-white/10 border border-white/20 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-foreground"
                              placeholder="0"
                              min={0}
                              step={1000000}
                            />
                          ) : (
                            <p className="text-lg font-bold text-accent-violet mt-0.5">
                              {formatCurrencyCompact(division.annualBudget)}
                            </p>
                          )}
                        </div>

                        {/* Fiscal Year */}
                        <div className="text-right min-w-[80px]">
                          <p className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">Fiscal Year</p>
                          {isEditing ? (
                            <select
                              value={editValues.budgetFiscalYear}
                              onChange={(e) => setEditValues(prev => ({ ...prev, budgetFiscalYear: e.target.value }))}
                              className="mt-1 w-full px-2 py-1 text-sm bg-white/10 border border-white/20 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-foreground"
                            >
                              {fiscalYearOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-base font-medium text-foreground mt-0.5">
                              {division.budgetFiscalYear ? `FY${division.budgetFiscalYear}` : '-'}
                            </p>
                          )}
                        </div>

                        {/* Programs Count */}
                        <div className="text-right min-w-[80px]">
                          <p className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">Programs</p>
                          <div className="flex items-center justify-end gap-1 mt-0.5">
                            <Users className="w-4 h-4 text-muted-foreground/60" />
                            <p className="text-base font-medium text-foreground">
                              {division._count?.programs || 0}
                            </p>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="min-w-[100px] text-right">
                          {summary && getStatusBadge(summary.status)}
                        </div>

                        {/* Actions */}
                        {canEdit && (
                          <div className="flex items-center gap-2">
                            {isEditing ? (
                              <>
                                <GlassButton
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleSaveEdit(division.id)}
                                  disabled={saving}
                                  leftIcon={saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                >
                                  Save
                                </GlassButton>
                                <GlassButton
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleCancelEdit}
                                  leftIcon={<X className="w-4 h-4" />}
                                >
                                  Cancel
                                </GlassButton>
                              </>
                            ) : (
                              <GlassButton
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStartEdit(division)}
                                leftIcon={<Edit className="w-4 h-4" />}
                              >
                                Edit
                              </GlassButton>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Progress bar when expanded */}
                    {summary && isExpanded && (
                      <div className="mt-4 ml-10">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-muted-foreground/60">Budget Utilization</span>
                          <span className="text-xs font-medium text-foreground">
                            {formatCurrencyCompact(summary.costs.committed)} / {formatCurrencyCompact(summary.budget.total)}
                          </span>
                        </div>
                        <ProgressBar
                          value={Math.min(summary.costs.committedPercent, 100)}
                          color={
                            summary.costs.committedPercent > 100 ? 'rose' :
                            summary.costs.committedPercent > 90 ? 'amber' : 'emerald'
                          }
                          size="sm"
                        />
                      </div>
                    )}
                  </div>

                  {/* Expanded Program Details */}
                  {isExpanded && summary && (
                    <div className="bg-white/5 border-t border-white/10">
                      <div className="p-5 pl-14">
                        {/* Summary Row */}
                        <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-white/5 rounded-lg">
                          <div>
                            <p className="text-xs text-muted-foreground/60 uppercase tracking-wide">Allocated to Programs</p>
                            <p className="text-lg font-semibold text-foreground">
                              {formatCurrencyCompact(summary.budget.allocatedToPrograms)}
                            </p>
                            <p className="text-xs text-muted-foreground/60">
                              {formatPercent(summary.budget.allocatedToProgramsPercent)} of budget
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground/60 uppercase tracking-wide">Committed Cost</p>
                            <p className="text-lg font-semibold text-accent-cyan">
                              {formatCurrencyCompact(summary.costs.committed)}
                            </p>
                            <p className="text-xs text-muted-foreground/60">
                              {formatPercent(summary.costs.committedPercent)} of budget
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground/60 uppercase tracking-wide">Spent</p>
                            <p className="text-lg font-semibold text-accent-emerald">
                              {formatCurrencyCompact(summary.costs.spent)}
                            </p>
                            <p className="text-xs text-muted-foreground/60">
                              {formatPercent(summary.costs.spentPercent)} of budget
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground/60 uppercase tracking-wide">Team Weeks Allocated</p>
                            <p className="text-lg font-semibold text-foreground">
                              {summary.capacity.totalTeamWeeksAllocated} TW
                            </p>
                          </div>
                        </div>

                        {/* Programs Table */}
                        <h4 className="text-sm font-semibold text-foreground mb-3">
                          Programs ({summary.programs.length})
                        </h4>
                        {summary.programs.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left py-2 px-3 text-muted-foreground/60 font-medium">Program</th>
                                  <th className="text-right py-2 px-3 text-muted-foreground/60 font-medium">Budget</th>
                                  <th className="text-right py-2 px-3 text-muted-foreground/60 font-medium">Labor</th>
                                  <th className="text-right py-2 px-3 text-muted-foreground/60 font-medium">Non-Labor</th>
                                  <th className="text-right py-2 px-3 text-muted-foreground/60 font-medium">Committed</th>
                                  <th className="text-right py-2 px-3 text-muted-foreground/60 font-medium">Spent</th>
                                  <th className="text-right py-2 px-3 text-muted-foreground/60 font-medium">Utilization</th>
                                  <th className="text-right py-2 px-3 text-muted-foreground/60 font-medium">TWs</th>
                                  <th className="text-right py-2 px-3 text-muted-foreground/60 font-medium">EPICs</th>
                                </tr>
                              </thead>
                              <tbody>
                                {summary.programs.map(program => (
                                  <tr key={program.programId} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="py-2 px-3 text-foreground font-medium">{program.programName}</td>
                                    <td className="py-2 px-3 text-right text-foreground">{formatCurrencyCompact(program.budget)}</td>
                                    <td className="py-2 px-3 text-right text-muted-foreground">{formatCurrencyCompact(program.laborBudget)}</td>
                                    <td className="py-2 px-3 text-right text-muted-foreground">{formatCurrencyCompact(program.nonLaborBudget)}</td>
                                    <td className="py-2 px-3 text-right text-accent-cyan">{formatCurrencyCompact(program.committedCost)}</td>
                                    <td className="py-2 px-3 text-right text-accent-emerald">{formatCurrencyCompact(program.spentCost)}</td>
                                    <td className="py-2 px-3 text-right">
                                      <span className={`font-medium ${
                                        program.budgetUtilizationPercent > 100 ? 'text-rose-500' :
                                        program.budgetUtilizationPercent > 90 ? 'text-amber-500' : 'text-foreground'
                                      }`}>
                                        {formatPercent(program.budgetUtilizationPercent)}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 text-right text-muted-foreground">{program.teamWeeksAllocated}</td>
                                    <td className="py-2 px-3 text-right text-muted-foreground">{program.epicCount}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted-foreground/60 text-sm italic">No programs in this division</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {divisions.length === 0 && (
              <div className="p-12 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground/60 mx-auto mb-4" />
                <p className="text-muted-foreground">No divisions found</p>
              </div>
            )}
          </div>
        </GlassCard>
      </SectionReveal>
    </div>
  );
};

export default DivisionBudgetManagementPage;
